// DesktopSidebar — navigation groupée par MODULE FONCTIONNEL coloré.
// Chaque bloc (Ads, Tracking, Stratégie, Cinema Studio, Compte) a sa couleur.
// Le solde de crédits est monté en haut, toujours visible.
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faRocket } from "@fortawesome/free-solid-svg-icons";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons";
import logo from "assets/logo.png";
import { getNavModules, isLinkActive } from "./navModules";
import CreditBalancePill from "./CreditBalancePill";
import { useLang } from "../../../../i18n/LanguageContext";
import LanguageSwitcher from "../../../../i18n/LanguageSwitcher";

const DesktopSidebar = ({ user, handleLogout, unreadCount = 0, onOpenOnboarding, persona = "default" }) => {
  const navModules = getNavModules(persona);
  const location = useLocation();
  const path = location.pathname;
  const { t } = useLang();

  return (
    <div className="hidden md:flex w-1/6 min-w-[210px] flex-col">
      <div className="flex flex-col h-full">
        {/* En-tête : logo + solde toujours visible */}
        <div className="mb-3">
          <img src={logo} alt="OnebotAds" className="h-8 mb-3" />
          <CreditBalancePill />
        </div>

        {/* Modules — chacun avec sa couleur */}
        <nav className="flex-1 overflow-y-auto pr-1 space-y-4">
          {navModules.map((mod) => (
            <div key={mod.key}>
              <div className="flex items-center gap-2 px-2 mb-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${mod.dot}`} />
                <span className={`text-[10px] uppercase font-bold tracking-wide ${mod.accent}`}>
                  {t(mod.label)}
                </span>
              </div>
              <ul className="space-y-0.5">
                {mod.links.map((link) => {
                  const active = isLinkActive(link, path);
                  return (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        data-tour={`nav:${link.to}`}
                        className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition ${
                          active
                            ? `${mod.activeBg} shadow-sm font-semibold`
                            : `text-gray-600 ${mod.hover}`
                        }`}
                      >
                        <FontAwesomeIcon icon={link.icon} className="text-xs w-4" />
                        <span className="truncate">{t(link.label)}</span>
                        {link.badge === "unread" && unreadCount > 0 && (
                          <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                            active ? "bg-white/25 text-white" : "bg-[#221C51] text-white"
                          }`}>
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Pied : utilisateur + upgrade + aide */}
        <div className="mt-3 p-3 bg-[#0A1A58]/10 rounded-2xl border border-[#0A1A58]/20">
          <Link to="/dashboard/profile" className="flex items-center gap-2 hover:opacity-75 transition">
            <FontAwesomeIcon icon={faUserCircle} className="text-2xl text-[#221C51]" />
            <div className="min-w-0">
              <p className="font-bold text-[#221C51] text-sm truncate">{user.name}</p>
              <p className="text-[11px] text-[#221C51]/70 truncate">{user.email}</p>
            </div>
          </Link>
          <Link
            to="/dashboard/billing"
            className="mt-2 w-full bg-[#F6F6F6] text-[#221C51] text-sm px-3 py-2 rounded-full hover:bg-gray-200 flex items-center justify-center gap-2 transition"
          >
            <FontAwesomeIcon icon={faRocket} className="text-sm" />
            {user.isPremium ? t("Gérer l'abonnement") : "Passer Pro"}
          </Link>
          {/* Bouton 🌐 de changement de langue */}
          <div className="mt-2 flex justify-center">
            <LanguageSwitcher compact className="w-full text-center" />
          </div>
          {onOpenOnboarding && (
            <button
              onClick={onOpenOnboarding}
              className="mt-1 w-full text-[#221C51]/50 text-xs hover:text-[#221C51] flex items-center justify-center gap-1 py-1"
            >
              <span>?</span><span>{t('Guide de démarrage')}</span>
            </button>
          )}
          <button
            onClick={() => handleLogout()}
            className="mt-1 w-full text-gray-400 text-xs hover:text-gray-600 flex items-center justify-center gap-1.5 py-1"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="text-xs" />
            <span>{t('Déconnexion')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
